import type { StateAction } from "@entrilia/shared/types";
import type { DimensionDictionary } from "../../../../../api/types/svTypes";
import type {
  SmartViewArtifactFilter,
  SmartViewArtifactVersion,
} from "../../../../../api/types/smartViewArtifactsTypes";

export interface ArtifactFilterState {
  artifactId: string;
  version: number;
  filters: SmartViewArtifactFilter[];
  values: Record<string, string[]>;
  labels: Record<string, Record<string, string>>;
}

export interface FilterLabelRequest {
  key: string;
  dataSetId: string;
  dimensionName: string;
  values: string[];
}

export const initialArtifactFilterState: ArtifactFilterState = {
  artifactId: "",
  version: 0,
  filters: [],
  values: {},
  labels: {},
};

const getOwn = <T>(record: Record<string, T>, key: string): T | undefined =>
  Object.hasOwn(record, key) ? record[key] : undefined;

const findFilter = (state: ArtifactFilterState, key: string): SmartViewArtifactFilter | undefined =>
  state.filters.find((filter) => filter.key === key);

const toAllowedValues = (filter: SmartViewArtifactFilter, values: string[]): string[] =>
  filter.kind === "Value" && filter.singleSelect ? values.slice(0, 1) : values;

const haveSameValues = (left: string[] | undefined, right: string[]): boolean =>
  left?.length === right.length && left.every((value, index) => value === right[index]);

const hasNewLabels = (existing: Record<string, string> | undefined, labels: Record<string, string>): boolean =>
  Object.entries(labels).some(([value, label]) => !existing || getOwn(existing, value) !== label);

export const applyDefaultsAction =
  (version: SmartViewArtifactVersion): StateAction<ArtifactFilterState> =>
  (state) => {
    if (state.artifactId === version.artifactId && state.version === version.version) {
      return state;
    }
    return {
      artifactId: version.artifactId,
      version: version.version,
      filters: version.filters,
      values: Object.fromEntries(
        version.filters.map((filter) => [filter.key, toAllowedValues(filter, filter.defaultValues)])
      ),
      labels: {},
    };
  };

export const setFilterAction =
  (key: string, values: string[]): StateAction<ArtifactFilterState> =>
  (state) => {
    const filter = findFilter(state, key);
    if (!filter || (filter.kind !== "Value" && values.length === 0)) {
      return state;
    }
    const allowedValues = toAllowedValues(filter, values);
    if (haveSameValues(getOwn(state.values, key), allowedValues)) {
      return state;
    }
    return { ...state, values: { ...state.values, [key]: allowedValues } };
  };

export const clearFilterAction =
  (key: string): StateAction<ArtifactFilterState> =>
  (state) =>
    findFilter(state, key)?.kind === "Value" ? setFilterAction(key, [])(state) : state;

export const addFilterLabelsAction =
  (key: string, labels: Record<string, string>): StateAction<ArtifactFilterState> =>
  (state) => {
    if (!findFilter(state, key)) {
      return state;
    }
    const existingLabels = getOwn(state.labels, key);
    if (!hasNewLabels(existingLabels, labels)) {
      return state;
    }
    return { ...state, labels: { ...state.labels, [key]: { ...existingLabels, ...labels } } };
  };

export const findFiltersNeedingLabels = (state: ArtifactFilterState): FilterLabelRequest[] =>
  state.filters.reduce<FilterLabelRequest[]>((requests, filter) => {
    if (filter.kind !== "Value") {
      return requests;
    }
    const values = getOwn(state.values, filter.key) ?? [];
    const labels = getOwn(state.labels, filter.key);
    const missingValues = values.filter((value) => !labels || !Object.hasOwn(labels, value));
    if (missingValues.length > 0) {
      requests.push({
        key: filter.key,
        dataSetId: filter.dataSetId,
        dimensionName: filter.dimensionName,
        values: missingValues,
      });
    }
    return requests;
  }, []);

export const buildFilterLabels = (
  values: string[],
  keyFieldName: string,
  rows: DimensionDictionary[]
): Record<string, string> => {
  const labelsByValue = new Map<string, string>();
  for (const row of rows) {
    const key = row[keyFieldName];
    if (key === null || key === undefined) {
      continue;
    }
    const name = row["Name"];
    labelsByValue.set(String(key), name === null || name === undefined || name === "" ? String(key) : String(name));
  }
  return Object.fromEntries(values.map((value) => [value, labelsByValue.get(value) ?? value]));
};
